import { ComponentType } from '@angular/cdk/portal';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { of, Subject, distinctUntilChanged, first, map, takeUntil } from 'rxjs';
import { AxiosError } from 'axios';
import { CopyCaseWithProductDialogComponent } from 'src/app/helper/copy-case-with-product-dialog/copy-case-with-product-dialog.component';
import { EditCaseDialogComponent } from 'src/app/helper/edit-case-dialog/edit-case-dialog.component';
import { EditFeesDialogComponent } from 'src/app/helper/edit-fees-dialog/edit-fees-dialog.component';
import { EditHiddenClaimsDialogComponent } from 'src/app/helper/edit-hidden-claims-dialog/edit-hidden-claims-dialog.component';
import { CasesService } from 'src/app/services/cases/cases.service';
import { ClientsService } from 'src/app/services/clients/clients.service';
import { SzamlazzhuService } from 'src/app/services/szamlazzhu/szamlazzhu.service';
import {
  AdditionalAction,
  AdditionalColumn,
} from 'src/app/shared/payee-cases/cases-table/cases-table.component';
import { PayeeCasesService } from 'src/app/shared/payee-cases/payee-cases.service';
import {
  CaseTableItem,
  ClientOption,
  GetCasesResponse,
} from 'src/app/shared/payee-cases/types';
import { AttorneyNamesColumnComponent } from './attorney-names-column/attorney-names-column.component';
import { IsDeletedColumnComponent } from './is-deleted-column/is-deleted-column.component';
import { MoreFilters } from './more-filters/more-filters';
import { OverwriteStateDialogComponent } from './overwrite-state-dialog/overwrite-state-dialog.component';
import { CreateTransitionLogDialogComponent } from './create-transition-log-dialog/create-transition-log-dialog.component';
import { SendCustomMessageDialogComponent } from './send-custom-message-dialog/send-custom-message-dialog.component';
import { PayeeCasesComponent } from '../../../shared/payee-cases/payee-cases.component';

export type AdminCaseTableItem = CaseTableItem & {
  payment_site_url: string | null;
  bulk_upload: string | null;
  has_processes: boolean;
  law_firm_names: string[];
  has_fees: boolean;
  client_is_duplicated: boolean;
  client_tax_number: string | null;
};

@Component({
  selector: 'app-cases',
  templateUrl: './cases.component.html',
  styleUrls: ['./cases.component.scss'],
  imports: [PayeeCasesComponent],
})
export class CasesComponent implements OnInit, OnDestroy {
  private casesService = inject(CasesService);
  private clientsService = inject(ClientsService);
  private dialog = inject(MatDialog);
  private payeeCasesService = inject(PayeeCasesService);
  private snackbar = inject(MatSnackBar);
  private szamlazzhuService = inject(SzamlazzhuService);
  private translate = inject(TranslateService);

  private readonly destroy = new Subject<void>();
  readonly getCasesPath = 'api/case';
  readonly getDebtorsPath = 'api/debtor';

  readonly additionalFilters: ComponentType<unknown>[] = [MoreFilters];

  additionalColumns: AdditionalColumn[] = [
    {
      columnDef: 'law_firms',
      sortable: null,
      cell: AttorneyNamesColumnComponent,
      header: of('Ügyvédi irodák'),
    },
  ];

  readonly additionalActions: AdditionalAction<AdminCaseTableItem>[] = [
    {
      label: of<string>('Ügy szerkesztése'),
      onClick: (element) => this.editCase(element),
      show: () => true,
      icon: 'pencil',
    },
    {
      label: of<string>('Ügy másolása'),
      onClick: (element) => this.copyCase(element),
      show: () => true,
      icon: 'copy-blue',
    },
    {
      label: of<string>('Adós oldal megnyitása'),
      onClick: (element) => this.openDebtorSite(element),
      show: (element) => !!element.payment_site_url,
      icon: 'external-link-blue',
    },
    {
      label: of<string>('Opcionális és díj követelések szerkesztése'),
      onClick: (element) => this.editHiddenClaims(element),
      show: () => true,
      icon: 'add-claim',
    },
    {
      label: of<string>('Díjak szerkesztése'),
      onClick: (element) => this.editFees(element),
      show: () => true,
      icon: 'add-claim',
    },
    {
      label: of<string>('Számlázz webhook újraküldése'),
      onClick: (element) => this.resendSzamlazzWebhook(element),
      show: (element) => element.partner?.integration === 'szamlazzhu',
      icon: 'document',
    },
    {
      label: this.translate.stream('CASES_TABLE.CASE_ACTIONS.RESEND_PROFORMA'),
      onClick: (element) => this.resendProForma(element),
      show: (element) => !!element.is_waiting_for_payment,
      icon: 'document',
    },
    {
      label: of<string>('Státusz megváltoztatása'),
      onClick: (element) => this.overwriteState(element),
      show: (element) => element.has_processes,
      icon: 'swap-status',
    },
    {
      label: of<string>('Esemény napló létrehozása'),
      onClick: (element) => this.createTransitionLog(element),
      show: (element) => element.has_processes,
      icon: 'document',
    },
    {
      label: of<string>('Ügyfélszolgálati kérés'),
      onClick: (element) => this.sendCustomMessage(element),
      show: () => true,
      icon: 'headset',
    },
  ];

  ngOnInit(): void {
    this.payeeCasesService.show_deleted
      .pipe(
        takeUntil(this.destroy),
        map((v) => (v === null ? null : +v)),
        distinctUntilChanged(),
      )
      .subscribe((v) => {
        if (v === 1) {
          this.additionalColumns.unshift({
            columnDef: 'is_deleted',
            sortable: null,
            cell: IsDeletedColumnComponent,
            header: of('Törölve?'),
          });
        } else {
          this.additionalColumns = this.additionalColumns.filter(
            (c) => c.columnDef !== 'is_deleted',
          );
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  readonly searchClients = async (name: string): Promise<ClientOption[]> => {
    const result = await this.clientsService.listClients({ name });
    return result.clients;
  };

  readonly resolveClient = async (
    uuid: string,
  ): Promise<ClientOption | null> => {
    try {
      return await this.clientsService.getClient(uuid);
    } catch {
      return null;
    }
  };

  async resendProForma(element: AdminCaseTableItem): Promise<void> {
    try {
      await this.casesService.resendProForma(element.id);
      this.snackbar.open(
        this.translate.instant(
          'CASES_TABLE.CASE_ACTIONS.RESEND_PROFORMA_SUCCESS',
        ),
        'OK',
        { duration: 2000 },
      );
      this.payeeCasesService.setCasesSubject.next();
    } catch {
      this.snackbar.open(
        this.translate.instant(
          'CASES_TABLE.CASE_ACTIONS.RESEND_PROFORMA_ERROR',
        ),
        'OK',
        { duration: 10_000 },
      );
    }
  }

  private editCase(element: AdminCaseTableItem): void {
    const ref = this.dialog.open(EditCaseDialogComponent, {
      data: element,
      autoFocus: false,
    });

    ref
      .afterClosed()
      .pipe(first())
      .subscribe(async (result?: GetCasesResponse) => {
        if (!result) {
          return;
        }

        await this.payeeCasesService.setCases();
      });
  }

  private copyCase(element: AdminCaseTableItem): void {
    const ref = this.dialog.open(CopyCaseWithProductDialogComponent, {
      data: element,
    });

    ref
      .afterClosed()
      .pipe(first())
      .subscribe((result?: AdminCaseTableItem) => {
        if (!result) {
          return;
        }

        this.payeeCasesService.payee_case_reference_id.next(
          result.payee_case_reference_id,
        );
      });
  }

  private editHiddenClaims(element: AdminCaseTableItem): void {
    const ref = this.dialog.open(EditHiddenClaimsDialogComponent, {
      data: element,
    });

    ref
      .afterClosed()
      .pipe(first())
      .subscribe(async (result?: boolean) => {
        if (!result) {
          return;
        }

        await this.payeeCasesService.setCases();
      });
  }

  private editFees(element: AdminCaseTableItem): void {
    this.dialog.open(EditFeesDialogComponent, {
      data: element,
      autoFocus: false,
    });
  }

  private async resendSzamlazzWebhook(
    element: AdminCaseTableItem,
  ): Promise<void> {
    try {
      await this.szamlazzhuService.resendWebhook(element.id);
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        console.error(
          'Network error while resending szamlazzhu webhook',
          error.response?.data,
        );
      } else {
        console.error(
          'Unknown error while resending szamlazzhu webhook',
          error,
        );
      }
    }
  }

  private openDebtorSite(element: AdminCaseTableItem): void {
    if (element.payment_site_url) {
      window.open(element.payment_site_url, '_blank');
    }
  }

  private overwriteState(element: AdminCaseTableItem): void {
    this.dialog.open(OverwriteStateDialogComponent, {
      data: element,
      autoFocus: false,
    });
  }

  private createTransitionLog(element: AdminCaseTableItem): void {
    this.dialog.open(CreateTransitionLogDialogComponent, {
      data: element,
      autoFocus: false,
    });
  }

  private sendCustomMessage(element: AdminCaseTableItem): void {
    this.dialog.open(SendCustomMessageDialogComponent, {
      data: element,
      autoFocus: false,
    });
  }
}
